import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Eye, Save, Plus, GripVertical, Trash2, Settings2,
  Palette, Layout, CheckCircle, Users, Copy, BarChart2,
  TrendingUp, Send, Clock, ExternalLink, Download, ChevronLeft, ChevronRight
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RT, ResponsiveContainer, Cell } from "recharts";
import * as XLSX from "xlsx";
import { Tooltip, TooltipTrigger, TooltipContent } from "../../components/ui/tooltip";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useAppearance } from "../../contexts/AppearanceContext";
import { useModules } from "../../contexts/ModulesContext";
import { toast } from "../../components/ui/sonner";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUBLIC_BASE = window.location.origin;

// Field groups: entity → system fields
const FIELD_GROUPS = {
  negocio: {
    label: "Negócios",
    system: [
      { field_name: "titulo",   label: "Título do negócio", type: "text",    entity: "negocio" },
      { field_name: "mensagem", label: "Mensagem / Obs.",   type: "textarea", entity: "negocio" },
    ],
  },
  empresa: {
    label: "Empresa",
    system: [
      { field_name: "empresa_nome",  label: "Nome da empresa", type: "text",  entity: "empresa" },
    ],
  },
  contato: {
    label: "Contatos",
    system: [
      { field_name: "nome",     label: "Nome completo", type: "text",  entity: "contato" },
      { field_name: "email",    label: "E-mail",        type: "email", entity: "contato" },
      { field_name: "telefone", label: "Telefone",      type: "phone", entity: "contato" },
      { field_name: "celular",  label: "Celular",       type: "phone", entity: "contato" },
    ],
  },
  produto: {
    label: "Produto",
    system: [
      { field_name: "produto_nome",  label: "Nome do produto", type: "text",    entity: "produto" },
      { field_name: "produto_preco", label: "Preço",           type: "currency", entity: "produto" },
    ],
  },
};

// FieldGroupPanel — collapsible, no colors
function FieldGroupPanel({ groupKey, group, customFields = [], usedFieldNames, onAdd, tc500 = "#3B82F6", visible = true }) {
  const [open, setOpen] = useState(groupKey === "contato");
  if (!visible) return null;
  const availableSystem = group.system.filter(f => !usedFieldNames.has(f.field_name));
  const availableCustom = customFields.filter(cf =>
    cf.module === groupKey + "s" || cf.module === groupKey ||
    (groupKey === "negocio" && cf.module === "negocios")
  ).filter(cf => !usedFieldNames.has(cf.field_name));

  if (availableSystem.length === 0 && availableCustom.length === 0) return null;

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 transition-colors">
        <span className="text-xs font-semibold text-slate-600">
          {group.label}
          <span className="ml-1.5 text-slate-400 font-normal">({availableSystem.length + availableCustom.length})</span>
        </span>
        <span className="text-[10px] text-slate-400">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="p-2 flex flex-wrap gap-1.5 border-t border-slate-100">
          {availableSystem.map(f => (
            <button key={f.field_name} type="button" onClick={() => onAdd({ ...f })}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-slate-200 rounded-md bg-white hover:bg-slate-50 text-slate-600 transition-colors">
              <Plus className="w-2.5 h-2.5" />{f.label}
            </button>
          ))}
          {availableCustom.map(cf => (
            <Tooltip key={cf.field_name}>
              <TooltipTrigger asChild>
                <button type="button"
                  onClick={() => onAdd({ field_name: cf.field_name, label: cf.field_label, type: cf.field_type, entity: groupKey, options: cf.options || [] })}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-white hover:bg-slate-50 text-slate-600 transition-colors"
                  style={{ border: `1px solid ${tc500}`, color: tc500 }}>
                  <Plus className="w-2.5 h-2.5" />{cf.field_label}
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">Campo personalizado</TooltipContent>
            </Tooltip>
          ))}
        </div>
      )}
    </div>
  );
}

const TABS = [
  { key: "campos",      label: "Campos",       icon: Layout },
  { key: "visual",      label: "Visual",        icon: Palette },
  { key: "pipeline",    label: "Pipeline",      icon: Settings2 },
  { key: "equipe",      label: "Equipe",        icon: Users },
  { key: "confirmacao", label: "Confirmação",   icon: CheckCircle },
  { key: "analytics",   label: "Analytics",     icon: BarChart2 },
];

const GOOGLE_FONTS = ["Inter", "Roboto", "Poppins", "Montserrat", "Lato", "Open Sans", "Raleway", "Nunito"];

export default function FormBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const appearance = useAppearance();
  const { modules } = useModules();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";

  const [form, setForm] = useState(null);
  const [activeTab, setActiveTab] = useState("campos");
  const [pipelines, setPipelines] = useState([]);
  const [stages, setStages] = useState([]);
  const [allCustomFields, setAllCustomFields] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [submissions, setSubmissions] = useState([]);
  const [subsPage, setSubsPage] = useState(1);
  const [subsTotal, setSubsTotal] = useState(0);
  const [subsTotalPages, setSubsTotalPages] = useState(1);

  // Load Google Font for live preview — declared after form state
  useEffect(() => {
    if (!form?.visual?.font) return;
    const fontId = `gfont-preview-${form.visual.font.replace(/\s/g, '-')}`;
    if (document.getElementById(fontId)) return;
    const link = document.createElement("link");
    link.id = fontId; link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(form.visual.font)}:wght@400;500;600;700&display=swap`;
    document.head.appendChild(link);
  }, [form?.visual?.font]);

  const load = useCallback(async () => {
    try {
      const [fRes, pipRes, cfRes, uRes] = await Promise.all([
        axios.get(`${API}/forms/${id}`, { withCredentials: true }),
        axios.get(`${API}/pipelines`, { withCredentials: true }),
        // Fetch custom fields for all modules
        axios.get(`${API}/custom-fields`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true }),
      ]);
      setForm(fRes.data);
      setPipelines(pipRes.data || []);
      setAllCustomFields(cfRes.data || []);
      setUsers((uRes.data || []).filter(u => u.status !== "inativo"));
      if (fRes.data.pipeline_id) {
        const sRes = await axios.get(`${API}/pipelines/${fRes.data.pipeline_id}/full`, { withCredentials: true });
        setStages((sRes.data?.stages || []).sort((a,b) => (a.order??0)-(b.order??0)));
      }
    } catch { /* silent */ }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const loadAnalytics = useCallback(async (d = analyticsDays) => {
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API}/forms/${id}/analytics?days=${d}`, { withCredentials: true });
      setAnalytics(res.data);
    } catch { /* silent */ }
    setAnalyticsLoading(false);
  }, [id, analyticsDays]);

  const loadSubmissions = useCallback(async (p = 1) => {
    try {
      const res = await axios.get(`${API}/forms/${id}/submissions?page=${p}&limit=20`, { withCredentials: true });
      setSubmissions(res.data.items || []);
      setSubsTotal(res.data.total || 0);
      setSubsTotalPages(res.data.pages || 1);
      setSubsPage(p);
    } catch { /* silent */ }
  }, [id]);

  useEffect(() => {
    if (activeTab === "analytics") {
      loadAnalytics(analyticsDays);
      loadSubmissions(1);
    }
  }, [activeTab, analyticsDays, loadAnalytics, loadSubmissions]);

  const exportSubmissions = async () => {
    try {
      const res = await axios.get(`${API}/forms/${id}/submissions?page=1&limit=9999`, { withCredentials: true });
      const all = res.data.items || [];
      if (!all.length) { toast.error("Nenhuma submissão para exportar."); return; }
      const rows = all.map(s => ({
        "Data/Hora": s.created_at ? new Date(s.created_at).toLocaleString("pt-BR") : "",
        "Nome": s.data?.nome || "",
        "E-mail": s.data?.email || "",
        "Telefone": s.data?.telefone || s.data?.celular || "",
        "Responsável": s.responsavel_nome || "",
        "Negócio": s.negocio_titulo || "",
        "IP": s.ip || "",
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Submissões");
      XLSX.writeFile(wb, `submissoes_${form?.slug || id}.xlsx`);
    } catch { toast.error("Erro ao exportar."); }
  };

  const save = async () => {    setSaving(true);
    try {
      const res = await axios.put(`${API}/forms/${id}`, form, { withCredentials: true });
      setForm(res.data);
      toast.success("Formulário salvo.");
    } catch { toast.error("Erro ao salvar."); }
    setSaving(false);
  };

  const setV = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const setVisual = (key, val) => setForm(prev => ({ ...prev, visual: { ...prev.visual, [key]: val } }));
  const setSuccess = (key, val) => setForm(prev => ({ ...prev, success: { ...prev.success, [key]: val } }));

  const addField = (fieldDef) => {
    if (form.fields.find(f => f.field_name === fieldDef.field_name)) {
      toast.error("Campo já adicionado."); return;
    }
    setV("fields", [...form.fields, {
      field_name: fieldDef.field_name,
      label: fieldDef.label,
      required: false,
      width: "100",
      placeholder: "",
      type: fieldDef.type || "text",
      entity: fieldDef.entity || "contato",
      options: fieldDef.options || [],
    }]);
  };

  const removeField = (idx) => setV("fields", form.fields.filter((_, i) => i !== idx));

  const updateField = (idx, key, val) => {
    const updated = [...form.fields];
    updated[idx] = { ...updated[idx], [key]: val };
    setV("fields", updated);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const updated = [...form.fields];
    const [moved] = updated.splice(result.source.index, 1);
    updated.splice(result.destination.index, 0, moved);
    setV("fields", updated);
  };

  const toggleResponsavel = (uid) => {
    const current = form.responsaveis || [];
    const updated = current.includes(uid) ? current.filter(r => r !== uid) : [...current, uid];
    setV("responsaveis", updated);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${PUBLIC_BASE}/f/${form.slug}`);
    toast.success("Link copiado!");
  };

  if (!form) {
    return <div className="flex items-center justify-center py-20 text-slate-400">Carregando...</div>;
  }

  const usedFieldNames = new Set(form.fields.map(f => f.field_name));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/configuracoes/formularios")}
          className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <input
            value={form.name}
            onChange={e => setV("name", e.target.value)}
            className="text-lg font-bold text-slate-900 font-heading bg-transparent outline-none border-b border-transparent hover:border-slate-300 focus:border-slate-400 w-full max-w-xs"
          />
          <p className="text-xs text-slate-400 font-mono mt-0.5">
            /f/{form.slug} —
            <button onClick={copyLink} className="ml-1 text-blue-500 hover:underline inline-flex items-center gap-1">
              <Copy className="w-2.5 h-2.5" />copiar link
            </button>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href={`/f/${form.slug}`} target="_blank" rel="noreferrer">
            <Button variant="outline" className="gap-1.5">
              <Eye className="w-3.5 h-3.5" />Pré-visualizar
            </Button>
          </a>
          <Button onClick={save} disabled={saving} className="gap-1.5 text-white" style={{ backgroundColor: tc500 }}>
            <Save className="w-3.5 h-3.5" />{saving ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === tab.key ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>
              <Icon className="w-3.5 h-3.5" />{tab.label}
            </button>
          );
        })}
      </div>

      <div className={`grid grid-cols-1 gap-5 ${activeTab === "analytics" ? "" : "lg:grid-cols-2"}`}>
        {/* ── LEFT: Editor ── */}
        <div className="space-y-4">

          {/* CAMPOS TAB */}
          {activeTab === "campos" && (
            <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Campos no formulário</p>
              </div>
              <div className="p-4 space-y-2">
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="form-fields">
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                        {form.fields.map((field, idx) => (
                          <Draggable key={field.field_name} draggableId={field.field_name} index={idx}>
                            {(p) => (
                              <div ref={p.innerRef} {...p.draggableProps}
                                className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                                <div {...p.dragHandleProps} className="cursor-grab text-slate-300 flex-shrink-0">
                                  <GripVertical className="w-4 h-4" />
                                </div>
                                {/* Entity badge removed — cleaner UI */}
                                <div className="flex-1 min-w-0 grid grid-cols-2 gap-2">
                                  <Input value={field.label} onChange={e => updateField(idx, "label", e.target.value)}
                                    className="h-7 text-xs" placeholder="Label" />
                                  <Input value={field.placeholder || ""} onChange={e => updateField(idx, "placeholder", e.target.value)}
                                    className="h-7 text-xs" placeholder="Placeholder" />
                                </div>
                                <Select value={field.width} onValueChange={v => updateField(idx, "width", v)}>
                                  <SelectTrigger className="w-20 h-7 text-xs"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="100">100%</SelectItem>
                                    <SelectItem value="50">50%</SelectItem>
                                  </SelectContent>
                                </Select>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  <span className="text-[10px] text-slate-400">Obrig.</span>
                                  <Switch checked={field.required} onCheckedChange={v => updateField(idx, "required", v)}
                                    className="scale-75" />
                                </div>
                                <button onClick={() => removeField(idx)} className="text-slate-300 hover:text-rose-500 flex-shrink-0">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {form.fields.length === 0 && (
                          <p className="text-xs text-slate-400 text-center py-4">Nenhum campo adicionado. Adicione abaixo.</p>
                        )}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
            </div>

              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Adicionar campos</p>
                </div>
                <div className="p-4 space-y-2">
                {Object.entries(FIELD_GROUPS).map(([key, group]) => (
                  <FieldGroupPanel
                    key={key}
                    groupKey={key}
                    group={group}
                    customFields={allCustomFields}
                    usedFieldNames={new Set(form.fields.map(f => f.field_name))}
                    onAdd={addField}
                    tc500={tc500}
                    visible={
                      key === "negocio" ? true :
                      key === "contato" ? modules.contatos !== false :
                      key === "empresa" ? modules.empresas !== false :
                      key === "produto" ? modules.produtos !== false : true
                    }
                  />
                ))}
                </div>
              </div>
            </div>
          )}

          {/* VISUAL TAB */}
          {activeTab === "visual" && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Personalização Visual</p>
              </div>
              <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Título público</Label>
                  <Input value={form.title} onChange={e => setV("title", e.target.value)} className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Subtítulo</Label>
                  <Input value={form.subtitle} onChange={e => setV("subtitle", e.target.value)} className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Cor de fundo</Label>
                  <div className="flex gap-2">
                    <input type="color" value={form.visual.bg_color} onChange={e => setVisual("bg_color", e.target.value)} className="h-8 w-10 rounded border border-slate-200 cursor-pointer" />
                    <Input value={form.visual.bg_color} onChange={e => setVisual("bg_color", e.target.value)} className="h-8 text-xs font-mono flex-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Cor do card</Label>
                  <div className="flex gap-2">
                    <input type="color" value={form.visual.card_color} onChange={e => setVisual("card_color", e.target.value)} className="h-8 w-10 rounded border border-slate-200 cursor-pointer" />
                    <Input value={form.visual.card_color} onChange={e => setVisual("card_color", e.target.value)} className="h-8 text-xs font-mono flex-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Cor do botão</Label>
                  <div className="flex gap-2">
                    <input type="color" value={form.visual.primary_color} onChange={e => setVisual("primary_color", e.target.value)} className="h-8 w-10 rounded border border-slate-200 cursor-pointer" />
                    <Input value={form.visual.primary_color} onChange={e => setVisual("primary_color", e.target.value)} className="h-8 text-xs font-mono flex-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Texto do botão</Label>
                  <Input value={form.visual.button_text} onChange={e => setVisual("button_text", e.target.value)} className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Fonte</Label>
                  <Select value={form.visual.font} onValueChange={v => setVisual("font", v)}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{GOOGLE_FONTS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Borda dos campos (px): {form.visual.border_radius}px</Label>
                  <input type="range" min="0" max="20" value={form.visual.border_radius}
                    onChange={e => setVisual("border_radius", Number(e.target.value))} className="w-full" />
                </div>
              </div>
              <div className="space-y-2">
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">URL do Logo</Label>
                  <Input value={form.visual.logo_url} onChange={e => setVisual("logo_url", e.target.value)} className="h-8 text-sm" placeholder="https://..." />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">URL do Banner / Imagem de capa</Label>
                  <Input value={form.visual.banner_url} onChange={e => setVisual("banner_url", e.target.value)} className="h-8 text-sm" placeholder="https://..." />
                </div>
              </div>
            </div>
            </div>
          )}

          {/* PIPELINE TAB */}
          {activeTab === "pipeline" && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Destino dos leads</p>
              </div>
              <div className="p-4 space-y-3">
                {(!form.pipeline_id || !form.stage_id) && (
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-700">
                    <span className="flex-shrink-0 mt-0.5">⚠️</span>
                    <span><strong>Pipeline não configurada.</strong> Os leads ficarão sem etapa e não aparecerão no Kanban. Configure abaixo.</span>
                  </div>
                )}
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Pipeline</Label>
                  <Select value={form.pipeline_id} onValueChange={async v => {
                    setV("pipeline_id", v); setV("stage_id", "");
                    const res = await axios.get(`${API}/pipelines/${v}/full`, { withCredentials: true });
                    setStages((res.data?.stages || []).sort((a,b) => (a.order??0)-(b.order??0)));
                  }}>
                    <SelectTrigger><SelectValue placeholder="Selecionar pipeline..." /></SelectTrigger>
                    <SelectContent>{pipelines.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Etapa de entrada</Label>
                  <Select value={form.stage_id} onValueChange={v => setV("stage_id", v)} disabled={!stages.length}>
                    <SelectTrigger><SelectValue placeholder="Selecionar etapa..." /></SelectTrigger>
                    <SelectContent>{stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* EQUIPE TAB — Round-robin */}
          {activeTab === "equipe" && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Distribuição Round-Robin</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Cada lead é atribuído ao próximo responsável em sequência.</p>
              </div>
              <div className="p-4 space-y-2">
                {users.map(u => (
                  <div key={u.user_id} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-slate-50">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: tc500 }}>
                      {u.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{u.name}</p>
                      <p className="text-xs text-slate-400 truncate">{u.email}</p>
                    </div>
                    <Switch
                      checked={(form.responsaveis || []).includes(u.user_id)}
                      onCheckedChange={() => toggleResponsavel(u.user_id)}
                    />
                  </div>
                ))}
              </div>
              {(form.responsaveis || []).length > 0 && (
                <div className="bg-slate-50 rounded-lg px-3 py-2 text-xs text-slate-500">
                  <strong>{(form.responsaveis || []).length}</strong> usuário{(form.responsaveis || []).length !== 1 ? "s" : ""} no pool de distribuição
                </div>
              )}
            </div>
          )}

          {/* ANALYTICS TAB */}
          {activeTab === "analytics" && (
            <div className="space-y-4 lg:col-span-2">

              {/* Period selector */}
              <div className="flex items-center justify-between">
                <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
                  {[7, 30, 90].map(d => (
                    <button key={d} type="button"
                      onClick={() => { setAnalyticsDays(d); loadAnalytics(d); }}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${analyticsDays === d ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>
                      {d}d
                    </button>
                  ))}
                </div>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={exportSubmissions}>
                  <Download className="w-3.5 h-3.5" />Exportar Excel
                </Button>
              </div>

              {/* KPI cards */}
              {analyticsLoading ? (
                <div className="flex items-center justify-center py-8 text-slate-400 text-sm">
                  <div className="animate-spin w-4 h-4 border-2 rounded-full mr-2" style={{ borderColor: `${tc500}40`, borderTopColor: tc500 }} />
                  Carregando métricas...
                </div>
              ) : analytics && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: "Visitas totais",    value: analytics.kpis.total_views,       icon: Eye,       color: "#3b82f6" },
                      { label: `Envios (${analyticsDays}d)`, value: analytics.kpis.period_submissions, icon: Send,      color: tc500 },
                      { label: "Conversão",         value: `${analytics.kpis.conversion_rate}%`, icon: TrendingUp, color: "#10b981" },
                      { label: "Total de envios",   value: analytics.kpis.total_submissions,  icon: BarChart2,  color: "#f59e0b" },
                    ].map(kpi => {
                      const Icon = kpi.icon;
                      return (
                        <div key={kpi.label} className="bg-white border border-slate-200 rounded-xl p-4">
                          <div className="flex items-start justify-between mb-2">
                            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide leading-tight">{kpi.label}</p>
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${kpi.color}18` }}>
                              <Icon className="w-3.5 h-3.5" style={{ color: kpi.color }} />
                            </div>
                          </div>
                          <p className="text-2xl font-bold text-slate-900">{kpi.value ?? "—"}</p>
                        </div>
                      );
                    })}
                  </div>

                  {/* Last submission */}
                  {analytics.kpis.last_submission && (
                    <p className="text-xs text-slate-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Último envio: {new Date(analytics.kpis.last_submission).toLocaleString("pt-BR")}
                    </p>
                  )}

                  {/* Chart — by day */}
                  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-100">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Envios por dia</p>
                    </div>
                    <div className="p-4">
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={analytics.by_day} barSize={8}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={d => d.slice(5)} interval={Math.floor(analytics.by_day.length / 8)} />
                          <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
                          <RT />
                          <Bar dataKey="count" name="Envios" radius={[2,2,0,0]}>
                            {analytics.by_day.map((entry, i) => (
                              <Cell key={i} fill={entry.count > 0 ? tc500 : `${tc500}30`} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Chart grid — weekday + hour */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                      <div className="px-4 py-3 border-b border-slate-100">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Por dia da semana</p>
                      </div>
                      <div className="p-4">
                        <ResponsiveContainer width="100%" height={140}>
                          <BarChart data={analytics.by_weekday} barSize={16}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
                            <RT />
                            <Bar dataKey="count" name="Envios" fill={tc500} radius={[2,2,0,0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                      <div className="px-4 py-3 border-b border-slate-100">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pico de horário</p>
                      </div>
                      <div className="p-4">
                        <ResponsiveContainer width="100%" height={140}>
                          <BarChart data={analytics.by_hour} barSize={6}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="label" tick={{ fontSize: 8 }} interval={3} />
                            <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
                            <RT />
                            <Bar dataKey="count" name="Envios" radius={[1,1,0,0]}>
                              {analytics.by_hour.map((h, i) => {
                                const max = Math.max(...analytics.by_hour.map(x => x.count));
                                const alpha = max > 0 ? Math.round(50 + (h.count / max) * 150).toString(16).padStart(2, "0") : "30";
                                return <Cell key={i} fill={`${tc500}${alpha}`} />;
                              })}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  {/* Round-robin distribution */}
                  {analytics.by_responsavel.length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                      <div className="px-4 py-3 border-b border-slate-100">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Distribuição por responsável</p>
                      </div>
                      <div className="p-4 space-y-2">
                        {analytics.by_responsavel.map(r => {
                          const totalResp = analytics.by_responsavel.reduce((a,b) => a + b.count, 0);
                          const pct = totalResp ? Math.round(r.count / totalResp * 100) : 0;
                          return (
                            <div key={r.responsavel_id}>
                              <div className="flex justify-between text-xs mb-1">
                                <span className="text-slate-600 font-medium">{r.nome}</span>
                                <span className="text-slate-500">{r.count} envios ({pct}%)</span>
                              </div>
                              <div className="h-1.5 bg-slate-100 rounded-full">
                                <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: tc500 }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Submissions table */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Submissões <span className="text-slate-400 font-normal ml-1">({subsTotal})</span>
                  </p>
                </div>
                {submissions.length === 0 ? (
                  <div className="text-center py-10 text-slate-400">
                    <Send className="w-7 h-7 mx-auto mb-2 text-slate-200" />
                    <p className="text-sm">Nenhuma submissão ainda.</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-100">
                          <tr>
                            <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase">Data</th>
                            <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase">Nome</th>
                            <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase hidden sm:table-cell">E-mail</th>
                            <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase hidden md:table-cell">Responsável</th>
                            <th className="px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase text-center">Negócio</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {submissions.map(s => (
                            <tr key={s.id} className="hover:bg-slate-50 transition-colors">
                              <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">
                                {s.created_at ? new Date(s.created_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                              </td>
                              <td className="px-4 py-2.5 font-medium text-slate-800 max-w-[150px] truncate">{s.data?.nome || "—"}</td>
                              <td className="px-4 py-2.5 text-slate-500 hidden sm:table-cell max-w-[180px] truncate">{s.data?.email || "—"}</td>
                              <td className="px-4 py-2.5 text-slate-500 hidden md:table-cell">{s.responsavel_nome || "—"}</td>
                              <td className="px-4 py-2.5 text-center">
                                {s.negocio_id ? (
                                  <a href={`/negocios?open=${s.negocio_id}`} target="_blank" rel="noreferrer"
                                    className="inline-flex items-center gap-1 text-xs font-medium hover:underline" style={{ color: tc500 }}>
                                    <ExternalLink className="w-3 h-3" />Ver
                                  </a>
                                ) : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {subsTotalPages > 1 && (
                      <div className="flex items-center justify-center gap-2 py-3 border-t border-slate-100">
                        <button onClick={() => loadSubmissions(subsPage - 1)} disabled={subsPage <= 1}
                          className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30 transition-colors">
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="text-xs text-slate-500">{subsPage} / {subsTotalPages}</span>
                        <button onClick={() => loadSubmissions(subsPage + 1)} disabled={subsPage >= subsTotalPages}
                          className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30 transition-colors">
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* CONFIRMAÇÃO TAB */}
          {activeTab === "confirmacao" && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Mensagem de Confirmação</p>
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Título</Label>
                  <Input value={form.success.title} onChange={e => setSuccess("title", e.target.value)} className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Mensagem</Label>
                  <textarea value={form.success.message} onChange={e => setSuccess("message", e.target.value)}
                    rows={3} className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg outline-none focus:border-slate-400 resize-none" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Redirecionar para URL (opcional)</Label>
                  <Input value={form.success.redirect_url} onChange={e => setSuccess("redirect_url", e.target.value)}
                    className="h-8 text-sm" placeholder="https://seusite.com/obrigado" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── RIGHT: Preview (hidden on analytics tab) ── */}
        <div className={`hidden lg:block ${activeTab === "analytics" ? "lg:hidden" : ""}`}>
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pré-visualização</p>
            </div>
            <div className="p-4">
              <div className="rounded-xl overflow-hidden border border-slate-100"
                style={{ background: form.visual.bg_color, fontFamily: `'${form.visual.font}', sans-serif` }}>
                {form.visual.banner_url && (
                  <img src={form.visual.banner_url} alt="banner" className="w-full h-28 object-cover" onError={e => e.target.style.display="none"} />
                )}
                <div className="p-4">
                  {form.visual.logo_url && (
                    <img src={form.visual.logo_url} alt="logo" className="h-8 mb-3 object-contain" onError={e => e.target.style.display="none"} />
                  )}
                  <div className="rounded-xl p-4 space-y-3" style={{ background: form.visual.card_color }}>
                    <div>
                      <h2 className="text-base font-bold text-slate-900">{form.title || form.name}</h2>
                      {form.subtitle && <p className="text-xs text-slate-500 mt-0.5">{form.subtitle}</p>}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {form.fields.map((field, i) => (
                        <div key={i} className={field.width === "50" ? "col-span-1" : "col-span-2"}>
                          <label className="block text-[11px] font-medium text-slate-700 mb-1">
                            {field.label}{field.required && <span className="text-rose-500 ml-0.5">*</span>}
                          </label>
                          {field.type === "textarea" ? (
                            <textarea rows={2} placeholder={field.placeholder}
                              className="w-full text-xs px-2.5 py-1.5 border border-slate-200 bg-slate-50 text-slate-400 resize-none"
                              style={{ borderRadius: form.visual.border_radius }} disabled />
                          ) : (
                            <input type="text" placeholder={field.placeholder}
                              className="w-full text-xs px-2.5 py-1.5 border border-slate-200 bg-slate-50 text-slate-400"
                              style={{ borderRadius: form.visual.border_radius }} disabled />
                          )}
                        </div>
                      ))}
                    </div>
                    {form.fields.length === 0 && (
                      <p className="text-xs text-slate-400 text-center py-2">Adicione campos para ver a pré-visualização</p>
                    )}
                    <button className="w-full py-2 text-xs font-semibold text-white"
                      style={{ backgroundColor: form.visual.primary_color, borderRadius: form.visual.border_radius }}>
                      {form.visual.button_text}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
